import React from 'react';

import Box from '@mui/material/Box';
import { keyframes } from '@mui/material/styles';

// A continuous highlight that flows left -> right (the standard animated-gradient
// shimmer: a wide multi-stop gradient at 200% size whose background-position shifts).
const flow = keyframes`
  0%   { background-position: -200% 0; }
  100% { background-position: 200% 0; }
`;

const animatedShimmer = {
  backgroundSize: '200% 100%',
  animation: `${flow} 4.5s linear infinite`,
};
// Static (no flow) — used at completion so a full bar reads as "done".
const staticShimmer = { backgroundSize: '200% 100%' };

// A determinate progress bar whose filled and unfilled portions both pulse in sync,
// flowing left -> right — so it animates even at 0%. Refined replacement for the
// default MUI buffer bar. Pass animate={false} to freeze the flow (e.g. when complete).
export default function FlowingProgress({ value, animate = true }: { value: number; animate?: boolean }) {
  const pct = Math.max(0, Math.min(100, Number.isFinite(value) ? value : 0));
  const shimmer = animate ? animatedShimmer : staticShimmer;
  return (
    <Box sx={{ position: 'relative', height: 8, borderRadius: 4, overflow: 'hidden' }}>
      {/* unfilled track — light indigo pulse */}
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          backgroundImage:
            'linear-gradient(90deg, #EEF2FF, #C7D2FE, #EEF2FF, #C7D2FE, #EEF2FF)',
          ...shimmer,
        }}
      />
      {/* filled portion — indigo pulse, synced to the track */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          bottom: 0,
          left: 0,
          width: `${pct}%`,
          borderRadius: 4,
          backgroundImage:
            'linear-gradient(90deg, #6366F1, #A5B4FC, #6366F1, #A5B4FC, #6366F1)',
          transition: 'width 0.4s ease',
          ...shimmer,
        }}
      />
    </Box>
  );
}
